/**
 * Yatay bir şeritte kaç öğenin sığdığını hesaplar; kalanlar "…" menüsüne iner.
 * Grid araç çubuğu ve üst menü aynı hesabı kullanıyor.
 *
 * Ölçüm iki aşamalı: öğeler ÖNCE tamamı görünürken ölçülüp genişlikleri saklanır, sonra
 * kesme uygulanır. Tek aşamalı ölçüm (gizle → yeniden ölç) salınıma giriyor: bir öğe
 * gizlenince yer açılıyor, geri geliyor, yine taşıyor.
 *
 * Görünmeyen şerit ÖLÇÜLMEZ (#462, 03.10): arka plandaki sekmede ya da açılırken gizli
 * kapta ölçülen genişlikler 0'dı ve bir kez ölçüldüğü için hiç düzelmiyordu — şerit sekme
 * öne gelince "hepsi sığıyor" sanıyor, "…" çıkmıyor, taşan düğmeler sağdaki ikonların
 * altında kesiliyordu. Ekrandaki öğeler de büyüyüp küçüldükçe (ikon / yazı tipi geç
 * yüklenir, sayaç rozeti çıkar) genişlikleri tazelenir; menüdeki öğelerin son ölçümü korunur.
 *
 * `itemCount`, kapsayıcı ya da ekrandaki öğelerin genişliği değişince yeniden hesaplanır.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

export interface UseOverflowCountOptions {
  /** Ölçülecek öğe sayısı. */
  itemCount: number;
  /** "…" düğmesi için ayrılan genişlik (px) — taşma varsa o da yer kaplar. */
  overflowButtonWidth?: number;
  /** Öğeler arası boşluk (px). */
  gap?: number;
  /** En az bu kadar öğe görünür kalsın. */
  minVisible?: number;
}

export function useOverflowCount(
  containerRef: RefObject<HTMLElement | null>,
  { itemCount, overflowButtonWidth = 40, gap = 4, minVisible = 0 }: UseOverflowCountOptions,
) {
  const [visibleCount, setVisibleCount] = useState(itemCount);
  /** Öğe genişlikleri: ekrandakiler her ölçümde tazelenir, menüdekiler son ölçümde kalır. */
  const widths = useRef<number[]>([]);
  const measured = useRef(false);

  // Öğe sayısı değişti: ölçümler geçersiz, hepsini göster ve yeniden ölç. İlk açılışta
  // çalışmaz — çalışsaydı ilk ölçümün kesmesini geri alıp hesabı ResizeObserver'a bırakırdı.
  const lastCount = useRef(itemCount);
  useLayoutEffect(() => {
    if (lastCount.current === itemCount) return;
    lastCount.current = itemCount;
    measured.current = false;
    widths.current = [];
    setVisibleCount(itemCount);
  }, [itemCount]);

  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    // Gizli kap (arka plandaki sekme, kapalı panel): ölçüm anlamsız, görünür olunca yapılır.
    const available = el.clientWidth;
    if (available <= 0) return;

    const children = Array.from(el.children) as HTMLElement[];
    // Ekrandaki öğelerin güncel genişliği: ilk `visibleCount` çocuk öğeler, sonrası "…".
    const shown = Math.min(visibleCount, itemCount, children.length);
    for (let i = 0; i < shown; i++) {
      const w = children[i]!.offsetWidth;
      if (w > 0) widths.current[i] = w;
    }

    if (!measured.current) {
      // İlk ölçüm yalnızca tüm öğeler ekrandayken ve hepsinin genişliği okunabiliyorken geçerli.
      if (children.length < itemCount) return;
      for (let i = 0; i < itemCount; i++) if (!(widths.current[i]! > 0)) return;
      measured.current = true;
    }

    let used = 0;
    let fits = 0;
    for (let i = 0; i < itemCount; i++) {
      const w = widths.current[i] ?? 0;
      const next = used + w + (i > 0 ? gap : 0);
      // Son öğe değilse "…" düğmesine de yer kalmalı.
      const reserve = i < itemCount - 1 ? overflowButtonWidth + gap : 0;
      if (next + reserve > available) break;
      used = next;
      fits++;
    }
    setVisibleCount(Math.max(minVisible, Math.min(itemCount, fits)));
  }, [containerRef, itemCount, gap, overflowButtonWidth, minVisible, visibleCount]);

  // Ölçüm boyama öncesinde yapılır: kullanıcı önce taşan, sonra kesilen bir çubuk görmesin.
  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  // Kap ve ekrandaki öğeler izlenir: kap daralınca / genişleyince, gizli kap görünür olunca,
  // bir düğme geç yüklenen ikonla büyüyünce hesap tazelenir.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
  }, [containerRef, recompute]);

  return visibleCount;
}
